import type { CreateClientRequest, RetrieveInviteResponse } from '@frontend/api-types';
import { some } from '@frontend/duck-tape';
import type { AccountCreationSchemaLegacy, useAccountCreationFormLegacy } from '@frontend/forms';
import { useMemo } from '@frontend/react';
import {
  Banner,
  BirthdayInput,
  Button,
  PhoneNumberInput,
  ScrollableContentPanel,
  Text,
  TextInput,
  XStack,
  YStack,
} from '@frontend/web-react';
import { NavLink } from '../../Navigation';
import { OnboardingStepHeader } from '../Onboarding/OnboardingStepHeader';

export type UserInfoStepState = Pick<
  CreateClientRequest,
  'birthday' | 'email' | 'firstName' | 'lastName' | 'nickname' | 'phoneNumber'
>;

type UserInfoProps = {
  disabledFields: Pick<Record<keyof AccountCreationSchemaLegacy, boolean>, 'email' | 'phoneNumber'>;
  errorBannerMessage?: string;
  form: ReturnType<typeof useAccountCreationFormLegacy>;
  incrementStep: () => void;
  inviteDetails?: RetrieveInviteResponse;
  isAnyLoading: boolean;
};

export const UserInfo = ({
  disabledFields,
  errorBannerMessage,
  form,
  incrementStep,
  inviteDetails,
  isAnyLoading,
}: UserInfoProps) => {
  const { errors, getControl } = form;
  const isInviteUnavailable = Boolean(errorBannerMessage);
  const isFormDisabled = isAnyLoading || isInviteUnavailable;

  const isValid = useMemo(
    () => !some([errors.email, errors.firstName, errors.lastName, errors.birthday, errors.phoneNumber]),
    [errors],
  );

  const onPressSubmit = () => incrementStep();

  return (
    <ScrollableContentPanel
      containerClassName="bg-cream-50"
      contentWidth="max-w-[488px] w-full"
      footer={
        <Button
          disabled={!isValid || isFormDisabled}
          fullWidth
          isLoading={isAnyLoading}
          label="Continue"
          onClick={onPressSubmit}
          variant="primary"
        />
      }
      header={<OnboardingStepHeader title="First things first" />}
      justifyContent="start"
    >
      <YStack gapY="md">
        {errorBannerMessage ? (
          <Banner color="danger" leftIconName="IconAlertCircle" title={errorBannerMessage} />
        ) : null}

        <YStack className="justify-center" gapY="sm">
          {/* If API provides this data, don't let user overwrite it */}
          <TextInput
            accessibilityLabel="Email"
            autoComplete="email"
            disabled={disabledFields.email || isFormDisabled}
            label={'Email'}
            labelTextProps={{ className: 'font-medium', type: 'h6' }}
            placeholder="Enter your email"
            {...getControl('email')}
          />
          {inviteDetails?.email ? (
            <XStack alignItems="center" gapX="xs" justifyContent="end">
              <Text>Wrong email?</Text>
              <NavLink bold={false} to="/login">
                Contact us
              </NavLink>
            </XStack>
          ) : null}
        </YStack>
        <TextInput
          accessibilityLabel="First name"
          autoComplete="given-name"
          disabled={isFormDisabled}
          label={'First name'}
          labelTextProps={{ className: 'font-medium', type: 'h6' }}
          placeholder="Enter your first name"
          {...getControl('firstName')}
        />
        <TextInput
          accessibilityLabel="Last name"
          autoComplete="family-name"
          disabled={isFormDisabled}
          label={'Last name'}
          labelTextProps={{ className: 'font-medium', type: 'h6' }}
          placeholder="Enter your last name"
          {...getControl('lastName')}
        />
        <BirthdayInput
          error={getControl('birthday').error}
          labelTextProps={{ className: 'font-medium' }}
          onChange={(val) => getControl('birthday').onChange(val as DateISOString)}
          value={getControl('birthday').value}
        />
        {/* If API provides this data, don't let user overwrite it */}
        <PhoneNumberInput
          disabled={disabledFields.phoneNumber || isFormDisabled}
          labelTextProps={{ className: 'font-medium', type: 'h6' }}
          {...getControl('phoneNumber')}
        />
      </YStack>
    </ScrollableContentPanel>
  );
};
